import React, { useEffect, useState, useRef, useMemo } from 'react';
import { BubbleInput, BubbleButton, BubbleAlert } from '@go-client/components/BubbleUI';
import { safeIdCompare, hasOwnProperty } from '@go-shared/lib/utils';
import { mapBy } from '@go-shared/lib/functional';

import { replaceTranslationByRefer, getTranslations } from '@go-client/lib/api/translation';
import { RouteComponentProps, Prompt } from 'react-router-dom';
import Fuse from 'fuse.js';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import TranslationBlock from '@go-client/components/TranslationBlock';
import Translations from './Translations';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  generateDefaultNewTranslation,
  makeCommonTextTranslationTransform,
  TranslationFilters,
} from '@go-shared/lib/translation';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BrandCommonText } from '@go-shared/types/brand';
import TranslationsSubNavigation from '../../components/Navigations/TranslationsSubNavigation';
import {
  CommonTextTransforms,
  CommonTextTranslatable,
  CommonTextTranslation,
} from '@go-shared/types/translation';
import { canModifyBrandTranslation } from '@go-shared/lib/permissions';
import { useUserInfo } from '@go-client/context/UserInfoContext';

interface CommonTextsTranslationDataBundle {
  commonTexts: BrandCommonText[];
  commonTextsTranslations: CommonTextTranslation[];
  commonTextsMap: { [key: string]: BrandCommonText };
  commonTextsTranslationsMap: { [key: string]: CommonTextTranslation };
}

interface CommonTextsTranslationForm {
  dirty: boolean;
  transforms: CommonTextTransforms;
}

interface ChangeCommonTextFormAction {
  active?: boolean;
  valueChange?: { key: keyof CommonTextTranslatable; value: string };
}

type CommonTextTranslationsPageProps = RouteComponentProps;

export default function CommonTextTranslations(props: CommonTextTranslationsPageProps) {
  const [loading, setLoading] = useState(true);
  const [dataBundle, setDataBundle] = useState<CommonTextsTranslationDataBundle | undefined>(
    undefined
  );
  const [activeCommonText, setActiveCommonText] = useState<BrandCommonText | undefined>(undefined);
  const [commonTextTranslationForm, setCommonTextTranslationForm] = useState<
    CommonTextsTranslationForm | undefined
  >(undefined);
  const [search, setSearch] = useState('');

  const contentRef = useRef<HTMLDivElement | null>(null);

  const { activeProperty } = useActiveProperty();

  const serverInfo = useServerInfo();
  const { brand, languages } = serverInfo;
  const { user } = useUserInfo();

  useEffect(() => {
    setLoading(true);
    fetchDataBundle().catch(console.error);
  }, [activeProperty]);

  // this will refresh the currently active day theme if data bundle refreshes
  useEffect(() => {
    if (!dataBundle) return;

    setActiveCommonText(state => {
      const brandCommonText = dataBundle.commonTexts[0];
      if (!state) return brandCommonText;
      const refreshedCommonText = dataBundle.commonTextsMap[state._id];
      return refreshedCommonText || brandCommonText;
    });
  }, [dataBundle]);

  // this will reset the form each time a new day theme is clicked
  useEffect(() => {
    resetCommonTextsForm();
  }, [activeCommonText]);

  // this will scroll the page to top when search is modified
  useEffect(() => {
    if (contentRef && contentRef.current) contentRef.current.scrollTop = 0;
  }, [search, contentRef]);

  const fuse = useMemo(() => {
    if (!dataBundle) return undefined;

    return new Fuse(dataBundle.commonTexts, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['name'],
    });
  }, [dataBundle]);

  const fuseSearchResults = useMemo(() => {
    if (!search || !fuse) return undefined;
    return fuse.search(search).map(s => s.item);
  }, [fuse, search]);

  const filteredCommonLabels = !dataBundle ? [] : fuseSearchResults || dataBundle.commonTexts;

  async function fetchDataBundle() {
    try {
      const commonTexts = brand.commonTexts;
      const commonLabelsTranslations = await getTranslations('commonText');

      const commonLabelsMap = mapBy(commonTexts, '_id');
      const commonLabelTranslationsMap = mapBy(commonLabelsTranslations, 'refers');

      setDataBundle({
        commonTexts: commonTexts,
        commonTextsTranslations: commonLabelsTranslations,
        commonTextsMap: commonLabelsMap,
        commonTextsTranslationsMap: commonLabelTranslationsMap,
      });
    } finally {
      setLoading(false);
    }
  }

  function resetCommonTextsForm() {
    const transforms = activeCommonText
      ? makeCommonTextTranslationTransform(
          languages.map(l => l.key),
          dataBundle?.commonTextsTranslationsMap[activeCommonText._id]
        )
      : undefined;

    if (!transforms) {
      setCommonTextTranslationForm(undefined);
      return;
    }

    const formData: CommonTextsTranslationForm = {
      dirty: false,
      transforms,
    };

    setCommonTextTranslationForm(formData);
  }

  function changeCommonTextForm(lang: string, change: ChangeCommonTextFormAction) {
    if (!commonTextTranslationForm) return;

    const updatedForm = { ...commonTextTranslationForm, dirty: true };

    if (!hasOwnProperty(updatedForm.transforms, lang)) {
      return;
    }

    updatedForm.transforms[lang].modified = true;

    if (change.active !== undefined) updatedForm.transforms[lang].active = change.active;
    if (change.valueChange !== undefined) {
      updatedForm.transforms[lang].delta[change.valueChange.key] = change.valueChange.value;
    }

    setCommonTextTranslationForm(updatedForm);
  }

  async function handleSubmitLevelForm() {
    if (
      !commonTextTranslationForm ||
      !activeCommonText ||
      !dataBundle ||
      !canModifyBrandTranslation(user, serverInfo)
    )
      return;

    const translation = generateDefaultNewTranslation(
      activeCommonText._id,
      'commonText',
      activeProperty._id
    );

    translation.transforms = commonTextTranslationForm.transforms;

    await replaceTranslationByRefer(activeCommonText._id, translation);
    await fetchDataBundle();
    setCommonTextTranslationForm({ ...commonTextTranslationForm, dirty: false });
  }

  function headerFn() {
    return <TranslationsSubNavigation active="commonTexts" property={activeProperty} />;
  }

  function leftFn() {
    return (
      <>
        <div
          className="sticky flex bg-white border-b"
          style={{ top: 'calc(130px + 40px + 0.75rem)', height: '50px', zIndex: 3 }}
        >
          <BubbleInput
            type="text"
            block
            appearance="bare"
            placeholder="Search..."
            className="flex-grow"
            inputStyle={{ height: '49px' }}
            inputClassName="px-5"
            value={search}
            onValue={setSearch}
          />
        </div>
        <div style={{ paddingTop: '50px' }}>
          {filteredCommonLabels.map(commonLabel => (
            <div
              key={commonLabel._id}
              className={`list-item px-5 py-3 cursor-pointer ${
                activeCommonText && safeIdCompare(activeCommonText._id, commonLabel._id)
                  ? 'active'
                  : ''
              }`}
              onClick={() => setActiveCommonText(commonLabel)}
            >
              <div className="flex items-center">
                <div className="overflow-ellipsis">
                  <div className="list-item-name font-semibold">{commonLabel.name}</div>
                </div>
                <FontAwesomeIcon
                  icon="chevron-right"
                  className="list-item-arrow ml-auto pl-3 flex-shrink-0"
                />
              </div>
            </div>
          ))}
          {filteredCommonLabels.length < 1 && (
            <div className="grid items-center justify-center italic text-smoke bg-eggshell px-3 py-2">
              No label found
            </div>
          )}
        </div>
      </>
    );
  }

  function rightFn({ selectedLanguages }: TranslationFilters) {
    return (
      <>
        {dataBundle && activeCommonText && commonTextTranslationForm && (
          <>
            <div style={{ marginBottom: '-1px' }}>
              {!canModifyBrandTranslation(user, serverInfo) && (
                <BubbleAlert className="mt-3 mx-3" variant="ocean">
                  This common text cannot be modified unless you're in a brand instance and a brand
                  admin.
                </BubbleAlert>
              )}
              <TranslationBlock
                label="Name"
                value={activeCommonText.name}
                languages={selectedLanguages}
                charCountFn={({ language }) => (
                  <span className="text-smoke-light">
                    ({commonTextTranslationForm.transforms[language.key].delta.name.length})
                  </span>
                )}
              >
                {({ language }) => (
                  <div className="flex items-start">
                    <BubbleButton
                      inverse
                      variant={
                        !commonTextTranslationForm.transforms[language.key].active
                          ? 'smoke'
                          : 'navy'
                      }
                      className="px-1-i pt-2 mr-2 leading-none"
                      onClick={() =>
                        changeCommonTextForm(language.key, {
                          active: !commonTextTranslationForm.transforms[language.key].active,
                        })
                      }
                      icon={
                        commonTextTranslationForm.transforms[language.key].active
                          ? 'eye'
                          : 'eye-slash'
                      }
                    />
                    <BubbleInput
                      type="text"
                      block
                      className="flex-grow"
                      inputClassName={
                        !commonTextTranslationForm.transforms[language.key].active
                          ? 'italic text-smoke'
                          : ''
                      }
                      value={commonTextTranslationForm.transforms[language.key].delta.name}
                      onValue={val =>
                        changeCommonTextForm(language.key, {
                          valueChange: { key: 'name', value: val },
                        })
                      }
                    />
                  </div>
                )}
              </TranslationBlock>
            </div>

            <div className="sticky bottom-0 p-3 bg-eggshell border-t">
              <BubbleButton
                onClick={() => handleSubmitLevelForm()}
                variant="navy"
                disabled={
                  !commonTextTranslationForm.dirty || !canModifyBrandTranslation(user, serverInfo)
                }
                icon="floppy-disk"
              >
                Save Changes
              </BubbleButton>
              <BubbleButton
                variant="slate"
                disabled={!commonTextTranslationForm.dirty}
                inverse
                onClick={() => resetCommonTextsForm()}
                icon="rotate-left"
              >
                Reset
              </BubbleButton>
            </div>
          </>
        )}
      </>
    );
  }

  // this adds a check to prevent closing tab if unsaved changes
  useEffect(() => {
    if (commonTextTranslationForm && commonTextTranslationForm.dirty) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
  });

  return (
    <>
      <Prompt
        when={commonTextTranslationForm && commonTextTranslationForm.dirty}
        message="You have unsaved changes! Are you sure you want to leave?"
      />
      <Translations
        {...props}
        loading={loading}
        contentRef={contentRef}
        category="commonTexts"
        property={activeProperty}
        languages={languages}
        leftFn={leftFn}
        rightFn={rightFn}
        headerFn={headerFn}
      />
    </>
  );
}
